import type { MeetingSettings } from '../models/meeting.model'

export const MEETING_STORAGE_KEYS = {
  RECORDS: 'inmeet_meetings',
  CREDENTIALS: 'inmeet_meeting_credentials',
  ACCESS_GRANTS: 'inmeet_meeting_access',
  JOIN_INTENT: 'inmeet_meeting_join_intent',
} as const

// Meeting codes are minted by the backend as 9-digit PINs
// (`meeting-pin.ts`: 100000000–999999999). This was 10, so every real code
// failed validation and the join button never enabled.
export const MEETING_ID_LENGTH = 9

// Title rules
export const MEETING_TITLE_MIN_LENGTH = 2
export const MEETING_TITLE_MAX_LENGTH = 50
export const MEETING_TITLE_REGEX = /^[a-zA-Z0-9 &''\-]+$/

// Participant display name
export const PARTICIPANT_NAME_MIN_LENGTH = 2
export const PARTICIPANT_NAME_MAX_LENGTH = 50
export const PARTICIPANT_NAME_REGEX = /^[a-zA-Z ]+$/

// Screen-share-only display name — the name a Screen Share session shows
// (docs/screen-share-only-join-checkpoint.md). Its own rule, wider than the one
// above on purpose: any script, accents and the punctuation real names carry
// ("J. O'Neil-Smith"). The backend and the mobile app check the same rule, and
// the account's own name is always accepted as it is (share-display-name.ts).
export const SHARE_DISPLAY_NAME_MIN_LENGTH = 2
export const SHARE_DISPLAY_NAME_MAX_LENGTH = 50
export const SHARE_DISPLAY_NAME_REGEX = /^[\p{L}\p{M} .'-]+$/u

// Duration rules
export const MEETING_DURATION_MIN_MINUTES = 5
export const MEETING_DURATION_MAX_MINUTES = 24 * 60

// Seats
export const MEETING_SEATS_MIN = 2
export const MEETING_SEATS_MAX_DEFAULT = 100

// Password rules
export const MEETING_PASSWORD_LENGTH = 8
export const MEETING_PASSWORD_MIN_LENGTH = 4
/** Any character is allowed; this bounds only how many of them there may be. */
export const MEETING_PASSWORD_MAX_LENGTH = 25
export const MEETING_PASSWORD_ALPHABET =
  'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%^&*'

// How long a single granted access stays valid before re-asking the password
// (only relevant to non-hosts on the same browser tab).
export const ACCESS_GRANT_TTL_MS = 6 * 60 * 60 * 1000 // 6 hours

// Buffer windows
/** A meeting is considered joinable this many minutes before its start. */
export const JOIN_EARLY_WINDOW_MS = 15 * 60 * 1000
/** A meeting is considered "expired" once it has ended this long ago. */
export const EXPIRY_GRACE_MS = 30 * 60 * 1000

export const DEFAULT_MEETING_SETTINGS: MeetingSettings = {
  passwordEnabled: false,
  waitingRoomEnabled: false,
  lockAfterStart: false,

  muteOnEntry: false,
  videoOnEntry: false,
  disableParticipantCamera: false,

  allowScreenShare: 'all',
  allowChat: true,
  allowReactions: true,
  allowParticipantRename: true,
  allowParticipantUnmute: true,

  autoRecord: false,
  transcriptionEnabled: false,

  allowJoinAnytime: false,
  allowLateJoin: true,
  requireApproval: false,

  reminderEnabled: false,
  reminderBeforeMinutes: 10,

  // On by default: simulcast is what lets a viewer take a lower rung for a
  // small tile. With it off a publisher sends one full-size stream that
  // nothing downstream can trim, which costs the most on exactly the
  // crowded calls it should help.
  simulcast: true,
  sipEnabled: false,
  telepresence: false,
}

export const MEETING_ERROR_CODES = {
  // Lookup
  MEETING_NOT_FOUND: 'MEETING_NOT_FOUND',
  MEETING_COMPLETED: 'MEETING_COMPLETED',
  MEETING_EXPIRED: 'MEETING_EXPIRED',
  MEETING_CANCELLED: 'MEETING_CANCELLED',
  MEETING_ENDED: 'MEETING_ENDED',
  MEETING_LOCKED: 'MEETING_LOCKED',
  MEETING_FULL: 'MEETING_FULL',
  MEETING_NOT_STARTED: 'MEETING_NOT_STARTED',
  LATE_JOIN_DISABLED: 'LATE_JOIN_DISABLED',
  // Mark as Past while people are still in the room
  MEETING_OCCUPIED: 'MEETING_OCCUPIED',

  // Recurring series: which day's meeting a link may open
  OCCURRENCE_OVER: 'OCCURRENCE_OVER',
  OCCURRENCE_CANCELLED: 'OCCURRENCE_CANCELLED',
  OCCURRENCE_NOT_TODAY: 'OCCURRENCE_NOT_TODAY',
  SERIES_ENDED: 'SERIES_ENDED',
  // Editing one occurrence of a series
  SERIES_FIELD: 'SERIES_FIELD',
  OCCURRENCE_PAST: 'OCCURRENCE_PAST',

  // Password
  PASSWORD_REQUIRED: 'PASSWORD_REQUIRED',
  INVALID_PASSWORD: 'INVALID_PASSWORD',

  // Form validation
  TITLE_REQUIRED: 'TITLE_REQUIRED',
  TITLE_TOO_SHORT: 'TITLE_TOO_SHORT',
  TITLE_INVALID_CHARS: 'TITLE_INVALID_CHARS',
  NAME_REQUIRED: 'NAME_REQUIRED',
  NAME_TOO_SHORT: 'NAME_TOO_SHORT',
  NAME_INVALID_CHARS: 'NAME_INVALID_CHARS',
  ID_REQUIRED: 'ID_REQUIRED',
  ID_INVALID_FORMAT: 'ID_INVALID_FORMAT',
  DATE_REQUIRED: 'DATE_REQUIRED',
  TIME_REQUIRED: 'TIME_REQUIRED',
  START_IN_PAST: 'START_IN_PAST',
  DURATION_INVALID: 'DURATION_INVALID',
  DURATION_TOO_LONG: 'DURATION_TOO_LONG',
  SEATS_INVALID: 'SEATS_INVALID',
  RECURRING_WEEKDAYS_REQUIRED: 'RECURRING_WEEKDAYS_REQUIRED',
  RECURRING_OCCURRENCES_INVALID: 'RECURRING_OCCURRENCES_INVALID',
  RECURRING_END_DATE_INVALID: 'RECURRING_END_DATE_INVALID',

  UNKNOWN: 'UNKNOWN',
} as const

export type MeetingErrorCode =
  (typeof MEETING_ERROR_CODES)[keyof typeof MEETING_ERROR_CODES]

export const MEETING_ERROR_MESSAGES: Record<MeetingErrorCode, string> = {
  [MEETING_ERROR_CODES.MEETING_NOT_FOUND]:
    "We couldn't find a meeting with that ID. Please check and try again.",
  [MEETING_ERROR_CODES.MEETING_COMPLETED]:
    'This meeting has already been completed.',
  [MEETING_ERROR_CODES.MEETING_EXPIRED]:
    'This meeting has already ended.',
  [MEETING_ERROR_CODES.MEETING_CANCELLED]:
    'This meeting was cancelled by the host.',
  [MEETING_ERROR_CODES.MEETING_ENDED]:
    'The host ended this meeting. It cannot be rejoined.',
  [MEETING_ERROR_CODES.MEETING_LOCKED]:
    'The host has locked this meeting. No one else can join right now.',
  [MEETING_ERROR_CODES.MEETING_FULL]:
    'This meeting has reached its maximum participant capacity. You cannot join at this time.',
  [MEETING_ERROR_CODES.MEETING_NOT_STARTED]:
    'This meeting has not started yet. Please join closer to the start time.',
  [MEETING_ERROR_CODES.LATE_JOIN_DISABLED]:
    'Late joining has been disabled for this meeting.',
  [MEETING_ERROR_CODES.MEETING_OCCUPIED]:
    'The meeting is currently live and in progress. You can mark it as finished only after ending the meeting.',
  [MEETING_ERROR_CODES.OCCURRENCE_OVER]: "Today's meeting is over.",
  [MEETING_ERROR_CODES.OCCURRENCE_CANCELLED]: "Today's meeting was cancelled.",
  [MEETING_ERROR_CODES.OCCURRENCE_NOT_TODAY]: "This meeting isn't today.",
  [MEETING_ERROR_CODES.SERIES_ENDED]: 'This meeting series has ended.',
  [MEETING_ERROR_CODES.SERIES_FIELD]:
    "The password and settings apply to every meeting and can't be changed for one day.",
  [MEETING_ERROR_CODES.OCCURRENCE_PAST]: 'This meeting has already happened and can no longer be changed.',

  [MEETING_ERROR_CODES.PASSWORD_REQUIRED]:
    'This meeting requires a password.',
  [MEETING_ERROR_CODES.INVALID_PASSWORD]:
    'Incorrect password. Please try again.',

  [MEETING_ERROR_CODES.TITLE_REQUIRED]: 'Meeting name is required.',
  [MEETING_ERROR_CODES.TITLE_TOO_SHORT]:
    'Meeting name must be at least 2 characters.',
  [MEETING_ERROR_CODES.TITLE_INVALID_CHARS]:
    "Only letters, numbers, spaces and & ' - are allowed.",
  [MEETING_ERROR_CODES.NAME_REQUIRED]: 'Name is required.',
  [MEETING_ERROR_CODES.NAME_TOO_SHORT]: 'Name must be at least 2 characters.',
  [MEETING_ERROR_CODES.NAME_INVALID_CHARS]: 'Name should contain alphabets only.',
  [MEETING_ERROR_CODES.ID_REQUIRED]: 'Meeting ID is required.',
  [MEETING_ERROR_CODES.ID_INVALID_FORMAT]:
    `Meeting ID must be exactly ${MEETING_ID_LENGTH} digits.`,
  [MEETING_ERROR_CODES.DATE_REQUIRED]: 'Please choose a date.',
  [MEETING_ERROR_CODES.TIME_REQUIRED]: 'Please choose a start time.',
  [MEETING_ERROR_CODES.START_IN_PAST]:
    'Start time must be in the future.',
  [MEETING_ERROR_CODES.DURATION_INVALID]: 'Minimum duration is 5 minutes.',
  [MEETING_ERROR_CODES.DURATION_TOO_LONG]:
    'Meetings cannot be longer than 24 hours.',
  [MEETING_ERROR_CODES.SEATS_INVALID]: 'Seat count is outside the allowed range.',
  [MEETING_ERROR_CODES.RECURRING_WEEKDAYS_REQUIRED]:
    'Pick at least one weekday for a weekly recurring meeting.',
  [MEETING_ERROR_CODES.RECURRING_OCCURRENCES_INVALID]:
    'Recurring meetings must end after at least one occurrence.',
  [MEETING_ERROR_CODES.RECURRING_END_DATE_INVALID]:
    "The end date can't be before the start date.",

  [MEETING_ERROR_CODES.UNKNOWN]: 'Something went wrong. Please try again.',
}
